import { comics } from "@/mocks/comics";
import { categories } from "@/mocks/categories";
import type { Comic, Chapter, Category } from "@/types/comic";

export const allComics: Comic[] = comics as Comic[];

export function getComicById(id?: string): Comic | undefined {
  if (!id) return undefined;
  return allComics.find((c) => c.id === id);
}

export function getCategoryById(id?: string): Category | undefined {
  if (!id) return undefined;
  return categories.find((c) => c.id === id);
}

export function getComicsByCategory(categoryId: string): Comic[] {
  return allComics.filter((c) => c.category === categoryId);
}

export function getComicsByIds(ids: string[]): Comic[] {
  return ids
    .map((id) => getComicById(id))
    .filter((c): c is Comic => Boolean(c));
}

export function getUniqueRegions(): string[] {
  return Array.from(new Set(allComics.map((c) => c.region)));
}

export function getUniqueYears(): number[] {
  return Array.from(new Set(allComics.map((c) => c.year))).sort((a, b) => b - a);
}

export function getRelatedComics(comic: Comic, limit = 4): Comic[] {
  const sameCategory = allComics.filter(
    (c) => c.id !== comic.id && c.category === comic.category
  );
  const sameTag = allComics.filter(
    (c) =>
      c.id !== comic.id &&
      c.category !== comic.category &&
      c.tags.some((t) => comic.tags.includes(t))
  );
  return [...sameCategory, ...sameTag].slice(0, limit);
}

export interface SearchFilters {
  keyword?: string;
  category?: string;
  status?: string;
  region?: string;
  sort?: string;
}

export function filterComics(filters: SearchFilters): Comic[] {
  const keyword = (filters.keyword || "").trim().toLowerCase();
  let result = allComics.filter((c) => {
    if (filters.category && filters.category !== "all" && c.category !== filters.category) {
      return false;
    }
    if (filters.status && filters.status !== "all" && c.status !== filters.status) {
      return false;
    }
    if (filters.region && filters.region !== "all" && c.region !== filters.region) {
      return false;
    }
    if (keyword) {
      const haystack = [c.title, c.author, c.region, ...c.tags, c.description]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }
    return true;
  });

  const sort = filters.sort || "updated";
  result = result.sort((a, b) => {
    if (sort === "views") return b.views - a.views;
    if (sort === "rating") return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  return result;
}

export function getFeaturedComics(limit = 4): Comic[] {
  return allComics.filter((c) => c.featured).slice(0, limit);
}

export function getNewComics(limit = 8): Comic[] {
  return [...allComics]
    .sort((a, b) => b.year - a.year || b.views - a.views)
    .slice(0, limit);
}

export function getHotComics(limit = 8): Comic[] {
  return [...allComics].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getCompletedComics(limit = 8): Comic[] {
  return allComics.filter((c) => c.status === "completed").slice(0, limit);
}

export function getChapters(comic: Comic): Chapter[] {
  const total = comic.chapterCount;
  const base = new Date(comic.updatedAt).getTime();
  const day = 24 * 60 * 60 * 1000;
  const list: Chapter[] = [];
  for (let i = total; i >= 1; i -= 1) {
    const date = new Date(base - (total - i) * day * 3);
    list.push({
      id: `ch-${i}`,
      title: `第 ${i} 话`,
      index: i,
      updatedAt: date.toISOString().slice(0, 10),
    });
  }
  return list;
}

export function getChapterByIndex(comic: Comic, chapterId?: string): Chapter | undefined {
  if (!chapterId) return undefined;
  return getChapters(comic).find((c) => c.id === chapterId);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1).replace(/\.0$/, "")}万`;
  }
  return String(views);
}

export const statusLabel = (status: string) => (status === "completed" ? "已完结" : "连载中");